import { useState } from 'react';
import { Search, ExternalLink, Loader2, Package, AlertTriangle, ImageOff } from 'lucide-react';
import { InstrumentLayout } from '@/components/growth-instruments';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import {
  searchProducts,
  isValidProductSearchResponse,
  WrongResponseTypeError,
  type ProductItem,
  type ProductSearchResponse,
} from '@/services/growth-instruments/ProductSearchService';

// Product card component
function ProductCard({ product }: { product: ProductItem }) {
  const hasImage = Boolean(product.image_url);
  
  return (
    <div className="flex gap-4 rounded-lg border border-border bg-card p-3 min-w-0">
      {/* Image thumbnail */}
      <div className="shrink-0 w-20 h-20 rounded-md overflow-hidden bg-muted flex items-center justify-center">
        {hasImage ? (
          <img
            src={product.image_url!}
            alt={product.title}
            className="w-full h-full object-cover"
            onError={(e) => {
              // Replace with placeholder on error
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
              target.parentElement?.classList.add('flex', 'items-center', 'justify-center');
              const icon = document.createElement('div');
              icon.innerHTML = '<svg class="w-8 h-8 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>';
              target.parentElement?.appendChild(icon);
            }}
          />
        ) : (
          <ImageOff className="w-8 h-8 text-muted-foreground" />
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 flex flex-col justify-between">
        <div className="space-y-1 min-w-0">
          {/* Title */}
          <h4 className="font-medium text-sm text-foreground line-clamp-2 break-words">
            {product.title}
          </h4>
          
          {/* Price and Source */}
          <div className="flex items-center gap-2 flex-wrap text-xs text-muted-foreground">
            {product.price && (
              <span className="font-semibold text-foreground">{product.price}</span>
            )}
            {product.source && (
              <span className="px-1.5 py-0.5 rounded bg-muted">{product.source}</span>
            )}
          </div>
        </div>

        {/* View link */}
        {product.product_url && (
          <a
            href={product.product_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs text-primary hover:underline mt-2"
          >
            <ExternalLink className="w-3 h-3" />
            View
          </a>
        )}
      </div>
    </div>
  );
}

// Loading skeleton for product cards
function ProductCardSkeleton() {
  return (
    <div className="flex gap-4 rounded-lg border border-border bg-card p-3">
      <Skeleton className="w-20 h-20 rounded-md shrink-0" />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-3 w-1/2" />
        <Skeleton className="h-3 w-1/4 mt-2" />
      </div>
    </div>
  );
}

// Empty state component
function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <Package className="w-12 h-12 text-muted-foreground/50 mb-4" />
      <p className="text-muted-foreground">No products found. Try changing keywords.</p>
    </div>
  );
}

// Error banner for wrong response type
function WrongResponseBanner() {
  return (
    <Alert variant="destructive" className="mb-4">
      <AlertTriangle className="h-4 w-4" />
      <AlertDescription>
        Wrong response type received. The search returned an unexpected format.
      </AlertDescription>
    </Alert>
  );
}

export default function ProductFinder() {
  // Input state
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('');
  const [region, setRegion] = useState('Bangladesh');
  
  // Search state
  const [searching, setSearching] = useState(false);
  const [result, setResult] = useState<ProductSearchResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [wrongResponseType, setWrongResponseType] = useState(false);

  const handleSearch = async () => {
    if (!query.trim()) return;
    
    setSearching(true);
    setError(null);
    setWrongResponseType(false);
    setResult(null);
    
    try {
      const searchResult = await searchProducts({
        query: query.trim(),
        category: category.trim() || undefined,
        region,
      });
      
      setResult(searchResult);
    } catch (err) {
      console.error('Search failed:', err);
      
      if (err instanceof WrongResponseTypeError) {
        setWrongResponseType(true);
        setError('The search returned an unexpected response format.');
      } else {
        setError(err instanceof Error ? err.message : 'Search failed. Please try again.');
      }
    } finally {
      setSearching(false);
    }
  };

  const canSearch = query.trim().length > 0;

  return (
    <InstrumentLayout
      title="Product Finder"
      description="Search for existing products to avoid reinventing the wheel."
      icon={Search}
    >
      <div className="space-y-6 w-full min-w-0">
        {/* Input Panel */}
        <div className="rounded-lg border border-border bg-card p-4 space-y-4 w-full min-w-0 overflow-hidden">
          <span className="text-sm font-medium text-foreground">Search Products</span>
          
          <div className="grid grid-cols-1 gap-4">
            {/* Search query */}
            <div className="space-y-1.5 min-w-0">
              <Label htmlFor="query" className="text-sm">
                What are you looking for?
              </Label>
              <Input
                id="query"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="e.g., wireless earbuds, note-taking app, standing desk..."
                className="w-full"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && canSearch && !searching) {
                    handleSearch();
                  }
                }}
              />
            </div>

            {/* Category (optional) */}
            <div className="space-y-1.5 min-w-0">
              <Label htmlFor="category" className="text-sm">
                Category (optional)
              </Label>
              <Input
                id="category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                placeholder="e.g., Electronics, Software, Furniture..."
                className="w-full"
              />
            </div>

            {/* Region */}
            <div className="space-y-1.5 min-w-0">
              <Label htmlFor="region" className="text-sm">
                Region
              </Label>
              <Input
                id="region"
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                placeholder="e.g., Bangladesh, USA, Global"
                className="w-full"
              />
            </div>
          </div>

          <Button 
            onClick={handleSearch} 
            disabled={!canSearch || searching}
            className="gap-2"
          >
            {searching ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Searching...
              </>
            ) : (
              <>
                <Search className="h-4 w-4" />
                Search Products
              </>
            )}
          </Button>
        </div>

        {/* Results Area */}
        <div className="w-full min-w-0 overflow-hidden">
          {/* Wrong response type banner */}
          {wrongResponseType && <WrongResponseBanner />}
          
          {/* Loading state */}
          {searching && (
            <div className="space-y-3">
              <ProductCardSkeleton />
              <ProductCardSkeleton />
              <ProductCardSkeleton />
              <ProductCardSkeleton />
            </div>
          )}
          
          {/* Error state */}
          {error && !searching && !wrongResponseType && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          
          {/* Results */}
          {result && !searching && (
            <div className="w-full min-w-0 overflow-auto max-h-[60vh]">
              {result.products.length === 0 ? (
                <EmptyState />
              ) : (
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground">
                    Found {result.products.length} product{result.products.length !== 1 ? 's' : ''}
                  </p>
                  <div className="grid grid-cols-1 gap-3">
                    {result.products.map((product, idx) => (
                      <ProductCard key={`${product.title}-${idx}`} product={product} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Initial empty state */}
          {!result && !searching && !error && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Package className="w-12 h-12 text-muted-foreground/30 mb-4" />
              <p className="text-muted-foreground text-sm">
                Enter a search query to find products
              </p>
            </div>
          )}
        </div>
      </div>
    </InstrumentLayout>
  );
}
